 import { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Calendar, Clock, MapPin, User, Phone, CreditCard } from 'lucide-react';
import api from '../../../api/axios';
import { AuthContext } from '../../../context/AuthContext';
import Header from '../../common/Header';
import Footer from '../../common/Footer';
import DeliveryChecker from './DeliveryChecker';
import PaymentGateway from '../../payment/PaymentGateway';

export default function TestBookingForm() {
  const { user } = useContext(AuthContext);
  const [selectedTests, setSelectedTests] = useState([]);
  const [formData, setFormData] = useState({
    date: '',
    time: '',
    address: '',
    latitude: null,
    longitude: null
  });
  const [deliveryInfo, setDeliveryInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [appointmentId, setAppointmentId] = useState(null);
  const [appointmentTotal, setAppointmentTotal] = useState(null);
  const [showPayment, setShowPayment] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Get selected tests from location state
    if (location.state?.selectedTests) {
      setSelectedTests(location.state.selectedTests);
    } else {
      // Redirect back if no tests selected
      navigate('/medical-tests');
    }
  }, [location.state, navigate]);

  const handleDeliveryCheck = (deliveryData) => {
    setDeliveryInfo(deliveryData);
    setFormData(prev => ({
      ...prev,
      latitude: deliveryData.coordinates.latitude,
      longitude: deliveryData.coordinates.longitude,
      // Auto-fill address from the typed/resolved address so user doesn't retype it
      ...(deliveryData.typedAddress && { address: deliveryData.typedAddress }),
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const validateForm = () => {
    if (!formData.date) {
      setError('Please select a date');
      return false;
    }
    
    if (!formData.time) {
      setError('Please select a time');
      return false;
    }
    
    if (!formData.address.trim()) {
      setError('Please enter your address');
      return false;
    }
    
    if (!formData.latitude || !formData.longitude) {
      setError('Please check delivery availability first');
      return false;
    }
    
    if (!deliveryInfo?.available) {
      setError('Home collection is not available for your location');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    try {
      const response = await api.post('/medical-tests/book/', {
        test_ids: selectedTests.map(test => test.id),
        date: formData.date,
        time: formData.time,
        address: formData.address,
        latitude: formData.latitude,
        longitude: formData.longitude
      });

      if (response.data.success) {
        const apptId = response.data.data.id;
        const total = response.data.data.total_amount || response.data.pricing_breakdown?.total_amount;
        setAppointmentId(apptId);
        setAppointmentTotal(total);
        setShowPayment(true);
      } else {
        setError('Booking failed. Please try again.');
      }
    } catch (error) {
      console.error('Booking error:', error);
      if (error.response?.data?.error) {
        setError(error.response.data.error);
      } else {
        setError('Booking failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSuccess = (paymentData) => {
    navigate('/medical-tests/booking-success', {
      state: {
        appointmentId,
        tests: selectedTests,
        total: appointmentTotal,
        paymentId: paymentData.razorpay_payment_id || 'PAID',
      }
    });
  };

  const handlePaymentError = (error) => {
    console.error('Payment failed:', error);
    setError(error.message || 'Payment failed. Please try again.');
    setShowPayment(false);
  };

  const calculateTotal = () => {
    const safeFloat = (val) => { const n = parseFloat(val); return isNaN(n) ? 0 : n; };
    const testsTotal = selectedTests.reduce((sum, test) => 
      sum + safeFloat(test.offer_price || test.price), 0);
    const deliveryCharge = deliveryInfo ? safeFloat(deliveryInfo.delivery_charge) : 0;
    return testsTotal + deliveryCharge;
  };

  const safePrice = (val) => { const n = parseFloat(val); return isNaN(n) ? 0 : n; };

  // Generate time slots
  const timeSlots = [];
  for (let hour = 7; hour <= 18; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      timeSlots.push(timeString);
    }
  }

  // Get minimum date (tomorrow)
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const minDate = tomorrow.toISOString().split('T')[0];

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4">
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Book Medical Test Appointment
            </h1>
            <p className="text-gray-600">
              Schedule home collection for your selected tests
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Booking Form */}
            <div className="lg:col-span-2 space-y-6">
              {/* Selected Tests Summary */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Selected Tests ({selectedTests.length})
                </h3>
                <div className="space-y-3">
                  {selectedTests.map((test) => (
                    <div key={test.id} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-b-0">
                      <div>
                        <h4 className="font-medium text-gray-900">{test.title}</h4>
                        <p className="text-sm text-gray-500">{test.description}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-green-600">
                          ₹{safePrice(test.offer_price || test.price).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Delivery Checker */}
              <DeliveryChecker onDeliveryCheck={handleDeliveryCheck} />

              {/* Appointment Form */}
              <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">
                  Appointment Details
                </h3>

                {error && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-red-600 text-sm">{error}</p>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Calendar className="w-4 h-4 inline mr-1" />
                      Preferred Date
                    </label>
                    <input
                      type="date"
                      name="date"
                      value={formData.date}
                      onChange={handleInputChange}
                      min={minDate}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  {/* Time */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Clock className="w-4 h-4 inline mr-1" />
                      Preferred Time
                    </label>
                    <select
                      name="time"
                      value={formData.time}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select time</option>
                      {timeSlots.map((time) => (
                        <option key={time} value={time}>
                          {time}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Address */}
                <div className="mt-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <MapPin className="w-4 h-4 inline mr-1" />
                    Collection Address
                  </label>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    placeholder="Enter your complete address for sample collection"
                    required
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Submit Button or Payment Gateway */}
                <div className="mt-8">
                  {!showPayment ? (
                    <button
                      type="submit"
                      disabled={loading || !deliveryInfo?.available}
                      className={`w-full py-3 px-4 rounded-lg font-semibold transition-all duration-200 ${
                        loading || !deliveryInfo?.available
                          ? 'bg-gray-400 text-white cursor-not-allowed'
                          : 'bg-blue-600 text-white hover:bg-blue-700 transform hover:scale-[1.02] active:scale-[0.98] shadow-lg hover:shadow-xl'
                      }`}
                    >
                      {loading ? (
                        <div className="flex items-center justify-center space-x-2">
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          <span>Booking Appointment...</span>
                        </div>
                      ) : (
                        'Book & Proceed to Payment'
                      )}
                    </button>
                  ) : (
                    <PaymentGateway
                      amount={parseFloat(appointmentTotal)}
                      serviceType="medical_test"
                      serviceId={appointmentId}
                      onSuccess={handlePaymentSuccess}
                      onError={handlePaymentError}
                      customerDetails={{
                        name: user?.name || user?.first_name || '',
                        email: user?.email || '',
                        mobile: user?.mobile || user?.phone || ''
                      }}
                    />
                  )}
                </div>
              </form>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow-sm p-6 sticky top-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Order Summary
                </h3>
                
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span>Tests ({selectedTests.length})</span>
                    <span>₹{selectedTests.reduce((sum, test) => 
                      sum + safePrice(test.offer_price || test.price), 0).toFixed(2)}</span>
                  </div>
                  
                  <div className="flex justify-between text-sm">
                    <span>Home Collection</span>
                    <span>
                      {deliveryInfo ? `₹${safePrice(deliveryInfo.delivery_charge).toFixed(2)}` : 'TBD'}
                    </span>
                  </div>
                  
                  <div className="border-t pt-3">
                    <div className="flex justify-between font-semibold text-lg">
                      <span>Total</span>
                      <span className="text-green-600">
                        ₹{deliveryInfo ? calculateTotal().toFixed(2) : 'TBD'}
                      </span>
                    </div>
                  </div>
                </div>

                {deliveryInfo?.available && (
                  <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <CreditCard className="w-5 h-5 text-green-600" />
                      <span className="text-sm font-medium text-green-800">
                        Home Collection Available
                      </span>
                    </div>
                    <p className="text-xs text-green-700 mt-1">
                      Sample collection within {deliveryInfo.estimated_time}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}